import { Descriptions } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { getNotice } from '../../../api/boardNotice.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import CampusDetailModal from '../../../components/modals/CampusDetailModal';
import GroupDetailModal from '../../../components/modals/GroupDetailModal';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import { IGetNotice } from '../../../models/boardNotice.model';
import { RootState } from '../../../store/rootReducer';

interface IMatch {
  noticeCode: string;
}

const BoardNoticeRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const noticeCode = Number(match.params.noticeCode);
  const history = useHistory();
  const location = useLocation();
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { groups } = useSelector((state: RootState) => state.group);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0004 = 지역코드
  const SY0004 = commonCodes.find((item) => item.commonCode === 'SY0004')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetNotice>();

  const [campusDetailVisible, setCampusDetailVisible] = useState(false);
  const [groupDetailVisible, setGroupDetailVisible] = useState(false);

  useEffect(() => {
    if (noticeCode) execute(getNotice(noticeCode));
  }, [noticeCode]);

  const getCampusInfo = (campusCode: string, type: string) => {
    const campus = campusData?.find((item: any) => item.campusCode === campusCode);

    switch (type) {
      case 'name':
        return campus?.campusKorName;
      case 'region': {
        const region = SY0004?.find(
          (item: any) => item.detailCode === campus?.sidoCode,
        );
        return region?.korName;
      }
    }
  };

  const getGroupName = (groupNo: number) => {
    const group = groups?.find((item: any) => item.groupNo === Number(groupNo));
    return group?.korName;
  };

  return (
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <SimpleHeader
        iconFile="icon-board-big.svg"
        parentMenu={t('common.menu.board')}
        currentMenu={t('board.notice.title4')}
      />

      <div className='comm-table__area rowType edit'>
        <Descriptions
          bordered
          column={3}
          size="small"
          labelStyle={{ width: '16%' }}
          contentStyle={{ width: '16%' }}
        >
          <Descriptions.Item label={t('common.column.title')} span={3}>
            {res?.data?.title}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.writer')}>
            {res?.data?.regMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('board.notice.column4')}>
            {moment(res?.data?.regDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('archive.learn.column3')}>
            {res?.data?.readCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('board.notice.column5')}>
            {
              res?.data?.allCampusYn === 'Y'
                ? t('common.text.all')
                : (
                  <a
                    className="download-link"
                    onClick={() => setCampusDetailVisible(true)}
                  >
                    {t('board.notice.option1')}
                  </a>
                  )
            }
          </Descriptions.Item>
          <Descriptions.Item label={t('board.notice.column6')}>
            {
              res?.data?.allGroupYn === 'Y'
                ? t('common.text.all')
                : (
                  <a
                    className="download-link"
                    onClick={() => setGroupDetailVisible(true)}
                  >
                    {t('board.notice.option1')}
                  </a>
                  )
            }
          </Descriptions.Item>
          <Descriptions.Item label={t('board.notice.column7')}>
            {res?.data?.openYn === 'Y' ? t('common.text.public') : t('common.text.private')}
          </Descriptions.Item>
          <Descriptions.Item label={`* ${t('common.column.contents')}`} span={3}>
            <div dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }} />
          </Descriptions.Item>
          {
            res?.data?.files
              ? (
                <Descriptions.Item label={t('archive.exam.column7')} span={3}>
                  {res.data.files.map((item: any) => (
                    <a
                      className="download-link btn-text mr-1"
                      href={item.fileLocation}
                      download={item.originalName}
                    >
                      {item.originalName}
                    </a>
                  ))}
                </Descriptions.Item>
                )
              : null
          }          
        </Descriptions>

        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className="btn-basic gray-line"
              onClick={() => history.push(`/board/notice${location.search}`)}
              text={t('common.button.list')}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink-line"
              onClick={() =>
                history.push(
                  `/board/notice/${noticeCode}/edit${location.search}`,
                )
              }
              text={t('common.button.edit')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {campusDetailVisible && (
        <CampusDetailModal
          campusDetailVisible={campusDetailVisible}
          setCampusDetailVisible={setCampusDetailVisible}
          getCampusInfo={getCampusInfo}
          noticeCode={noticeCode}
        />
      )}
      {groupDetailVisible && (
        <GroupDetailModal
          groupDetailVisible={groupDetailVisible}
          setGroupDetailVisible={setGroupDetailVisible}
          getGroupName={getGroupName}
          noticeCode={noticeCode}
        />
      )}
    </div>
  );
};

export default BoardNoticeRead;
